/**
 * A note's markdown (g76): a small subset, parsed once and read two ways.
 *
 * `renderNote` builds the note as nodes for the places that show it rendered
 * (a view link's Details, and g77's viewer); `notePlainText` gives the words
 * with the markup removed, for a row's two-line preview. Both walk the same
 * parse, so the two cannot disagree about what is text and what is markup.
 *
 * The subset, and nothing else: paragraphs split on a blank line, a single
 * newline as a line break (notes are typed on phones, so this departs from
 * CommonMark on purpose), `#` to `###` headings, `-`, `*` and `1.` lists,
 * `**bold**`, `*italic*` and `_italic_`, `` `code` ``, `[label](url)` and bare
 * `http(s)://` addresses. Anything outside it is its own literal text: never
 * dropped, and never markup. Every node is built with `el()` and text nodes;
 * no HTML string exists anywhere on this path, so a note that holds
 * `<script>` shows the word.
 *
 * Only `http:`, `https:` and `mailto:` targets become links, judged on the
 * parsed `URL` rather than the typed string, since the parser strips the
 * whitespace and tabs a string check would miss.
 *
 * The parse is linear in the note's length: one pass over the lines, and one
 * scan per paragraph whose every lookahead stops at the next bracket or line.
 */

import { el, setText } from "./dom";

type Inline =
  | { kind: "text"; text: string }
  | { kind: "break" }
  | { kind: "strong"; children: Inline[] }
  | { kind: "em"; children: Inline[] }
  | { kind: "code"; text: string }
  | { kind: "link"; href: string; children: Inline[] };

type Block =
  | { kind: "paragraph"; inline: Inline[] }
  | { kind: "heading"; depth: 1 | 2 | 3; inline: Inline[] }
  | { kind: "list"; ordered: boolean; start: number; items: Inline[][] };

/**
 * The tag a note's `#` takes: one below the heading of whatever holds it, so
 * the page keeps a valid outline. Details sits under the panel's `h2`; the
 * g77 viewer under the page's `h1`.
 */
export type HeadingLevel = 2 | 3;

/**
 * Whether an item has a note worth reading (g77): the one rule the item
 * menus' View note and the viewer itself share. Whitespace alone is none.
 */
export function hasNote(note: string | null | undefined): boolean {
  return Boolean(note?.trim());
}

/** A row shows two lines; this is well past two lines at the widest pane. */
export const PREVIEW_MAX = 400;

const HEADING_TAGS = ["h2", "h3", "h4", "h5"] as const;
const LINK_SCHEMES = new Set(["http:", "https:", "mailto:"]);
const ESCAPABLE = new Set("\\`*_[]()#+-.!<>{}|~");
/** A bare address starts only after one of these, or at the start. */
const URL_BOUNDARY = new Set(["(", "<", "*", "_", "\"", "'"]);
const URL_TRAILING = new Set([".", ",", ":", ";", "!", "?", "'", "\"", "*", "_"]);

const HEADING = /^ {0,3}(#{1,3})[ \t]+(.*)$/;
const LIST_ITEM = /^[ \t]*(?:([-*])|(\d{1,9})\.)[ \t]+(\S.*)$/;

export function renderNote(source: string, options: { headingLevel: HeadingLevel }): HTMLDivElement {
  const offset = options.headingLevel - 2;
  // The note's outline continues the page's (g77): a heading is never more
  // than one level below the one before it, so a note that opens with `##`,
  // or goes from `#` to `###`, still reads as a valid outline. 0 is the
  // heading of whatever holds the note.
  let above = 0;
  return el("div", {
    class: "note-body",
    attrs: { "data-heading-level": String(options.headingLevel) },
    children: parse(source).map((block) => {
      if (block.kind !== "heading") return renderBlock(block, offset);
      const depth = Math.min(block.depth, above + 1) as 1 | 2 | 3;
      above = depth;
      return renderBlock({ ...block, depth }, offset);
    }),
  });
}

/**
 * The note's words with the markup gone: a heading's hashes and a list's
 * markers dropped, a link reduced to its label, and every line break and
 * block boundary a single space. Cut at `PREVIEW_MAX`, never inside a
 * surrogate pair.
 */
export function notePlainText(source: string): string {
  const parts: string[] = [];
  for (const block of parse(source)) {
    for (const inline of block.kind === "list" ? block.items : [block.inline]) {
      plainInline(inline, parts);
      parts.push(" ");
    }
  }
  const text = parts.join("").replace(/\s+/g, " ").trim();
  if (text.length <= PREVIEW_MAX) return text;
  const cut = text.slice(0, PREVIEW_MAX);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

const previewed = new WeakMap<HTMLElement, string | null>();

/**
 * Writes a row's note preview: the plain text, hidden when there is none.
 * Parsed again only when the note changed, since a row repaints on every
 * delta for any of its fields and a note may be 10,000 characters; and the
 * text node is kept when the words are the same, so a selection in it
 * survives a repaint for something else.
 */
export function paintNotePreview(node: HTMLElement, note: string | null): void {
  if (previewed.has(node) && previewed.get(node) === note) return;
  previewed.set(node, note);
  const text = note ? notePlainText(note) : "";
  setText(node, text);
  node.hidden = !text;
}

function plainInline(nodes: Inline[], parts: string[]): void {
  for (const node of nodes) {
    if (node.kind === "text" || node.kind === "code") parts.push(node.text);
    else if (node.kind === "break") parts.push(" ");
    else plainInline(node.children, parts);
  }
}

function renderBlock(block: Block, offset: number): HTMLElement {
  if (block.kind === "paragraph") return el("p", { children: renderInline(block.inline) });
  if (block.kind === "heading") {
    return el(HEADING_TAGS[offset + block.depth - 1]!, { children: renderInline(block.inline) });
  }
  return el(block.ordered ? "ol" : "ul", {
    attrs: { start: block.ordered && block.start !== 1 ? block.start : undefined },
    children: block.items.map((item) => el("li", { children: renderInline(item) })),
  });
}

function renderInline(nodes: Inline[]): (Node | string)[] {
  return nodes.map((node) => {
    switch (node.kind) {
      case "text": return node.text;
      case "break": return el("br");
      case "code": return el("code", { text: node.text });
      case "strong": return el("strong", { children: renderInline(node.children) });
      case "em": return el("em", { children: renderInline(node.children) });
      case "link": return el("a", {
        attrs: { href: node.href, target: "_blank", rel: "noopener noreferrer" },
        children: renderInline(node.children),
      });
    }
  });
}

// ---------------------------------------------------------------- blocks

type Open =
  | { kind: "paragraph"; lines: string[] }
  | { kind: "list"; ordered: boolean; start: number; items: string[][] };

function parse(source: string): Block[] {
  const blocks: Block[] = [];
  let open: Open | undefined;
  const close = (): void => {
    if (open?.kind === "paragraph") blocks.push({ kind: "paragraph", inline: parseInline(open.lines.join("\n")) });
    else if (open) {
      blocks.push({
        kind: "list",
        ordered: open.ordered,
        start: open.start,
        items: open.items.map((lines) => parseInline(lines.join("\n"))),
      });
    }
    open = undefined;
  };

  for (const line of source.replace(/\r\n?/g, "\n").split("\n")) {
    const trimmed = line.trim();
    if (trimmed === "") {
      close();
      continue;
    }
    const heading = HEADING.exec(line);
    const headingText = heading?.[2]!.trim();
    if (heading && headingText) {
      close();
      blocks.push({ kind: "heading", depth: heading[1]!.length as 1 | 2 | 3, inline: parseInline(headingText) });
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      const ordered = item[2] !== undefined;
      if (open?.kind !== "list" || open.ordered !== ordered) {
        close();
        open = { kind: "list", ordered, start: ordered ? Number(item[2]) : 1, items: [] };
      }
      open.items.push([item[3]!.trim()]);
      continue;
    }
    // An indented line under an item carries on that item; nested lists are
    // out of scope, so it is never a list of its own.
    if (open?.kind === "list" && /^[ \t]/.test(line)) {
      open.items[open.items.length - 1]!.push(trimmed);
    } else if (open?.kind === "paragraph") {
      open.lines.push(trimmed);
    } else {
      close();
      open = { kind: "paragraph", lines: [trimmed] };
    }
  }
  close();
  return blocks;
}

// ---------------------------------------------------------------- inlines

type Delim = { kind: "delim"; char: "*" | "_"; size: 1 | 2; canOpen: boolean; canClose: boolean; role?: "open" | "close" };
type Token = Inline | Delim;

const isSpace = (char: string | undefined): boolean => char === undefined || /\s/.test(char);
const isWord = (char: string | undefined): boolean => char !== undefined && /[\p{L}\p{N}]/u.test(char);

function parseInline(source: string, links = true): Inline[] {
  const tokens: Token[] = [];
  let text = "";
  const flush = (): void => {
    if (text) tokens.push({ kind: "text", text });
    text = "";
  };
  const push = (token: Token): void => {
    flush();
    tokens.push(token);
  };
  const codeRuns = backtickRuns(source);

  let i = 0;
  while (i < source.length) {
    const char = source[i]!;

    if (char === "\\" && i + 1 < source.length && ESCAPABLE.has(source[i + 1]!)) {
      text += source[i + 1];
      i += 2;
      continue;
    }

    if (char === "`") {
      let size = 1;
      while (source[i + size] === "`") size++;
      const end = codeRuns.closing(i, size);
      if (end === undefined) {
        text += "`".repeat(size);
      } else {
        push({ kind: "code", text: source.slice(i + size, end) });
        size += end - i;
      }
      i += size;
      continue;
    }

    // An image is not in the subset: the whole of it is its own text, so
    // nothing inside it becomes a link.
    if (char === "!" && source[i + 1] === "[") {
      const link = matchLink(source, i + 1);
      if (link) {
        text += source.slice(i, link.end);
        i = link.end;
      } else {
        text += "!";
        i++;
      }
      continue;
    }

    if (char === "[" && links) {
      const link = matchLink(source, i);
      if (link) {
        const href = allowedHref(link.target);
        if (href !== undefined && link.label.trim() !== "") {
          push({ kind: "link", href, children: parseInline(link.label, false) });
        } else {
          text += source.slice(i, link.end);
        }
        i = link.end;
        continue;
      }
    }

    if ((char === "h" || char === "H") && links && (i === 0 || isSpace(source[i - 1]) || URL_BOUNDARY.has(source[i - 1]!))) {
      const bare = matchBareUrl(source, i);
      if (bare) {
        if (bare.href !== undefined) push({ kind: "link", href: bare.href, children: [{ kind: "text", text: bare.text }] });
        else text += bare.text;
        i += bare.text.length;
        continue;
      }
    }

    if (char === "*" || char === "_") {
      let size = 1;
      while (source[i + size] === char) size++;
      if ((char === "*" && size <= 2) || (char === "_" && size === 1)) {
        const before = source[i - 1];
        const after = source[i + size];
        let canOpen = !isSpace(after);
        let canClose = i > 0 && !isSpace(before);
        // `snake_case` stays a word: an underscore inside one is neither.
        if (char === "_") {
          canOpen &&= !isWord(before);
          canClose &&= !isWord(after);
        }
        if (canOpen || canClose) {
          push({ kind: "delim", char, size: size as 1 | 2, canOpen, canClose });
          i += size;
          continue;
        }
      }
      text += char.repeat(size);
      i += size;
      continue;
    }

    if (char === "\n") {
      push({ kind: "break" });
      i++;
      continue;
    }

    text += char;
    i++;
  }
  flush();
  matchDelims(tokens);
  return build(tokens);
}

/**
 * Pairs emphasis. A closer takes the nearest open opener of its own kind, and
 * any opener of another kind opened after that one can no longer close inside
 * it, so spans nest and never cross. Every opener is pushed and popped once.
 */
function matchDelims(tokens: Token[]): void {
  const stacks = new Map<string, number[]>([["*1", []], ["*2", []], ["_1", []]]);
  tokens.forEach((token, index) => {
    if (token.kind !== "delim") return;
    const stack = stacks.get(`${token.char}${token.size}`)!;
    if (token.canClose && stack.length > 0) {
      const opener = stack.pop()!;
      for (const other of stacks.values()) {
        while (other.length > 0 && other[other.length - 1]! > opener) other.pop();
      }
      (tokens[opener] as Delim).role = "open";
      token.role = "close";
    } else if (token.canOpen) {
      stack.push(index);
    }
  });
  // An opener still on a stack was never closed, and an opener dropped for
  // crossing never got a role: both fall through to text in `build`.
}

function build(tokens: Token[]): Inline[] {
  const root: Inline[] = [];
  const frames: { kind: "strong" | "em"; children: Inline[]; parent: Inline[] }[] = [];
  let current = root;
  const append = (node: Inline): void => {
    const last = current[current.length - 1];
    if (node.kind === "text" && last?.kind === "text") last.text += node.text;
    else current.push(node);
  };
  for (const token of tokens) {
    if (token.kind !== "delim") {
      append(token);
    } else if (token.role === "open") {
      const frame = { kind: token.size === 2 ? "strong" as const : "em" as const, children: [], parent: current };
      frames.push(frame);
      current = frame.children;
    } else if (token.role === "close") {
      const frame = frames.pop()!;
      current = frame.parent;
      append({ kind: frame.kind, children: frame.children });
    } else {
      append({ kind: "text", text: token.char.repeat(token.size) });
    }
  }
  return root;
}

/**
 * Where each code span closes: the next run of exactly as many backticks on
 * the same line. One pointer per run length only moves forward, so finding
 * every span costs one pass over the runs.
 */
function backtickRuns(source: string): { closing(start: number, size: number): number | undefined } {
  const runs = new Map<number, { start: number; line: number }[]>();
  const breaks: number[] = [];
  for (let i = 0; i < source.length; i++) {
    if (source[i] === "\n") breaks.push(i);
    if (source[i] !== "`") continue;
    let size = 1;
    while (source[i + size] === "`") size++;
    if (!runs.has(size)) runs.set(size, []);
    runs.get(size)!.push({ start: i, line: breaks.length });
    i += size - 1;
  }
  const next = new Map<number, number>();
  // The inline scan asks in order of `start`, so the opener's line only moves forward too.
  let line = 0;
  return {
    closing(start, size) {
      while (line < breaks.length && breaks[line]! < start) line++;
      const list = runs.get(size);
      if (!list) return undefined;
      let at = next.get(size) ?? 0;
      while (at < list.length && list[at]!.start <= start) at++;
      next.set(size, at);
      const candidate = list[at];
      return candidate && candidate.line === line ? candidate.start : undefined;
    },
  };
}

/**
 * `[label](target)` starting at `start`, or nothing. The label stops at the
 * first unescaped `]`, `[` or line end; the target at whitespace, a bracket or
 * an unbalanced `)`. Each scan ends where the next `[` begins, so no stretch of
 * the note is scanned twice.
 */
function matchLink(source: string, start: number): { label: string; target: string; end: number } | undefined {
  let i = start + 1;
  while (i < source.length) {
    const char = source[i]!;
    if (char === "\\" && i + 1 < source.length && source[i + 1] !== "\n") {
      i += 2;
      continue;
    }
    if (char === "]" || char === "[" || char === "\n") break;
    i++;
  }
  if (source[i] !== "]" || source[i + 1] !== "(") return undefined;
  const labelEnd = i;
  i += 2;
  const targetStart = i;
  let depth = 0;
  while (i < source.length) {
    const char = source[i]!;
    if (/\s/.test(char) || char === "[" || char === "]") return undefined;
    if (char === "(") depth++;
    else if (char === ")") {
      if (depth === 0) {
        return { label: source.slice(start + 1, labelEnd), target: source.slice(targetStart, i), end: i + 1 };
      }
      depth--;
    }
    i++;
  }
  return undefined;
}

function allowedHref(target: string): string | undefined {
  try {
    const url = new URL(target);
    return LINK_SCHEMES.has(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A bare `http://` or `https://` address at `start`: up to whitespace or an
 * angle bracket, less the punctuation that ends the sentence around it and a
 * `)` it did not open. `href` is missing when it does not parse as one, and
 * the text then stays text.
 */
function matchBareUrl(source: string, start: number): { text: string; href: string | undefined } | undefined {
  const head = source.slice(start, start + 8).toLowerCase();
  if (!head.startsWith("http://") && !head.startsWith("https://")) return undefined;
  let end = start;
  let depth = 0;
  while (end < source.length && !/[\s<>]/.test(source[end]!)) {
    if (source[end] === "(") depth++;
    else if (source[end] === ")") depth--;
    end++;
  }
  while (end > start) {
    const last = source[end - 1]!;
    if (URL_TRAILING.has(last)) end--;
    else if (last === ")" && depth < 0) {
      end--;
      depth++;
    } else break;
  }
  const text = source.slice(start, end);
  let href: string | undefined;
  try {
    const url = new URL(text);
    if ((url.protocol === "http:" || url.protocol === "https:") && url.hostname) href = url.href;
  } catch {
    href = undefined;
  }
  return { text, href };
}
